import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, AlertTriangle, Send, MessageCircle, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/authContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const TYPE_STYLES = {
  DUVIDA: { label: "Dúvida", color: "bg-blue-500/15 text-blue-700 border-blue-500/30" },
  ORACAO: { label: "Oração", color: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  CRISE: { label: "CRISE", color: "bg-rose-500/15 text-rose-700 border-rose-500/40" },
  GERAL: { label: "Geral", color: "bg-slate-500/15 text-slate-700 border-slate-500/30" },
};

const PRIORITY_STYLES = {
  normal: "bg-slate-100 text-slate-600",
  alta: "bg-amber-100 text-amber-700",
  urgente: "bg-rose-100 text-rose-700",
};

const SAMPLE_PROMPTS = [
  { key: "duvida", label: "Dúvida bíblica", body: "O que a Bíblia diz sobre perdoar quem nos magoa profundamente?" },
  { key: "oracao", label: "Pedido de oração", body: "Por favor orem pela minha mãe que está hospitalizada." },
  { key: "geral", label: "Mensagem geral", body: "Bom dia, pastor! Estou começando a conhecer o ministério." },
  { key: "crise", label: "Crise (escala alerta)", body: "Pastor, eu não aguento mais... já pensei em acabar com tudo. Estou desesperado." },
];

export default function AdminTestWebhook() {
  const { isAuthenticated, user, isChecking } = useAuth();
  const [phone, setPhone] = useState("+5511999000111");
  const [name, setName] = useState("Maria de Teste");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]); // recent results

  if (isChecking) {
    return <div className="max-w-3xl mx-auto p-8 text-navy/55">Verificando…</div>;
  }
  if (!isAuthenticated) return <Navigate to="/entrar?next=/admin/test-webhook" replace />;
  if (user?.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="test-webhook-no-access">
        <p className="font-display text-2xl text-navy">Acesso restrito</p>
        <p className="text-sm text-navy/55 mt-2">Apenas administradores podem acessar essa rota.</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!phone.trim() || !body.trim()) {
      toast.error("Preencha telefone e mensagem.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/webhook/whatsapp/test", {
        phone: phone.trim(),
        name: name.trim(),
        body: body.trim(),
      });
      const result = r.data;
      setHistory((h) => [
        { ...result, sentMessage: body.trim(), at: new Date().toISOString() },
        ...h,
      ].slice(0, 8));
      setBody("");
      if (result.classification?.type === "CRISE") {
        toast.warning("Crise detectada, alerta criado.");
      } else {
        toast.success(`Resposta gerada (${result.classification?.type}).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao chamar o webhook.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto section-padding py-8" data-testid="test-webhook-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/65 hover:text-gold transition-colors mb-6"
        data-testid="test-webhook-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar ao painel
      </Link>

      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Módulo 1 · Teste</p>
        <h1 className="font-display text-3xl sm:text-4xl text-navy mt-2">Webhook WhatsApp · IA Pastoral</h1>
        <p className="text-sm text-navy/65 mt-3 max-w-2xl">
          Simule o recebimento de uma mensagem WhatsApp. A IA classifica em
          <strong> DÚVIDA / ORAÇÃO / CRISE / GERAL </strong>
          e responde com tom pastoral. Mensagens de crise criam um alerta e disparam e-mail para o pastor.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white border border-border rounded-2xl p-6 sm:p-7 space-y-5"
        data-testid="test-webhook-form"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-navy/60">Telefone (E.164)</span>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="test-webhook-phone"
              placeholder="+5511999000111"
              className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-navy/60">Nome do contato</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="test-webhook-name"
              placeholder="Ex.: Maria"
              className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-navy/60">Mensagem (do usuário)</span>
          <Textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="test-webhook-body"
            placeholder="Digite a mensagem que o usuário enviaria pelo WhatsApp…"
            className="mt-2 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest text-navy/55">Sugestões:</span>
          {SAMPLE_PROMPTS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setBody(p.body)}
              data-testid={`test-webhook-sample-${p.key}`}
              className="text-[11px] px-3 py-1.5 rounded-full bg-[hsl(var(--parchment))] border border-border text-navy/75 hover:border-gold/60 transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex justify-end pt-1">
          <Button
            type="submit"
            disabled={loading || !body.trim() || !phone.trim()}
            data-testid="test-webhook-submit"
            className="bg-navy hover:bg-gold hover:text-navy text-white px-6 py-3 rounded-full text-[11px] uppercase tracking-widest"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar para a IA
          </Button>
        </div>
      </form>

      {history.length > 0 && (
        <section className="mt-10" data-testid="test-webhook-history">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Últimas respostas</p>
          <div className="space-y-4">
            {history.map((h, idx) => {
              const cls = h.classification || {};
              const ts = TYPE_STYLES[cls.type] || TYPE_STYLES.GERAL;
              return (
                <article
                  key={h.interaction_id || idx}
                  data-testid={`test-webhook-result-${idx}`}
                  className={`bg-white border rounded-2xl p-5 sm:p-6 ${cls.type === "CRISE" ? "border-rose-500/40" : "border-border"}`}
                >
                  <header className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-widest ${ts.color}`}>
                        {ts.label}
                      </span>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${PRIORITY_STYLES[cls.priority] || ""}`}>
                        {cls.priority}
                      </span>
                      {cls.prayer_registered && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wider">
                          <Sparkles className="w-3 h-3" /> Oração registrada
                        </span>
                      )}
                      {h.alert_created && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[10px] uppercase tracking-wider">
                          <AlertTriangle className="w-3 h-3" /> Alerta {h.email_sent ? "+ e-mail" : "criado"}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-navy/45">id: {h.interaction_id?.slice(0, 8)}…</span>
                  </header>

                  <div className="bg-[hsl(var(--parchment))] rounded-xl p-3.5 mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-navy/55 mb-1.5">
                      <MessageCircle className="w-3 h-3 inline mr-1" /> Mensagem do usuário
                    </p>
                    <p className="text-sm text-navy/85 whitespace-pre-line">{h.sentMessage}</p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gold mb-1.5">
                      <Sparkles className="w-3 h-3 inline mr-1" /> Resposta da IA
                    </p>
                    <p className="text-sm text-navy/90 leading-relaxed whitespace-pre-line" data-testid={`test-webhook-ai-response-${idx}`}>
                      {cls.response}
                    </p>
                  </div>

                  {cls.escalate && cls.escalate_reason && (
                    <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3">
                      <p className="text-[10px] uppercase tracking-widest text-rose-700 mb-1">Motivo da escalada</p>
                      <p className="text-[12px] text-rose-900">{cls.escalate_reason}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
